import React from 'react'
import { AppState, Guardian, Student, Teacher, useAppContext, User } from '../../providers/context'
import { useTranslation } from '../../providers/translation'
import { Step, Tour } from '../../components/tour'
import { ComposeState } from './compose/composeTypes'
import { usePusher } from '../../providers/pusher'
import { showImperitiveTooltip } from '../../components/simple/tooltip'
import { Toasts } from '../../providers/toast'
import { isMobileSize } from '../../utils'
import { URLAction, useURLParams } from '../../providers/URLParams'
import { useShowWithPriority } from '../../providers/modalPriorityProvider'
import { makeRequest, showAxiosErrorAsToast } from '../../request'

export const HomeTutorial: React.FC<{
  user: User
  launchImmediately?: boolean
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
}> = ({ user, setComposeState, launchImmediately }) => {
  const [tourState, setTourState] = React.useState({ open: false, stepIndex: 0 })
  const showTutorial = () => {
    if (!isMobileSize()) {
      setTourState({ open: true, stepIndex: 0 })
    } else
      Toasts.showToast({
        subject: 'Tutorial skipped.',
        message: "We can't display our tutorial on mobile. Find it later from the lifesaver in the top right corner.",
      })
  }
  const URLParams = useURLParams()
  React.useEffect(() => {
    if (URLParams.pop(URLAction.OpenTutorial)) showTutorial()
  }, [URLParams])

  React.useEffect(() => {
    if (launchImmediately) showTutorial()
  }, [launchImmediately])

  if (!tourState.open) return null

  return (
    <HomeTutorialElement
      user={user}
      setComposeState={setComposeState}
      tourState={tourState}
      setTourState={setTourState}
    />
  )
}

const HomeTutorialElement: React.FC<{
  user: User
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
  tourState: { open: boolean; stepIndex: number }
  setTourState: React.Dispatch<React.SetStateAction<{ open: boolean; stepIndex: number }>>
}> = ({ user, setComposeState, tourState, setTourState }) => {
  const { tr } = useTranslation()
  const { state, setState } = useAppContext()

  // show a tooltip when the tutorial is closed
  const mountTime = React.useRef(Date.now())
  React.useEffect(() => {
    return () => {
      if (Date.now() - mountTime.current < 500) return // stop showing the tooltip if closed instantly, this stops strict mode from triggering it too
      showImperitiveTooltip('#tutorial_menuTooltip', tr('You can find tutorials at a later time from the help menu.'), {
        showForMs: 3000,
        overwriteExisting: true,
      })
    }
  }, [])

  const tourSteps = React.useMemo(
    () => getTourSteps(user, state, tr, setComposeState),
    [user, state, tr, setComposeState]
  )

  usePusher('message' + user.id, 'rmt', () => {
    if (tourSteps[tourState.stepIndex].title === 'Waiting for Response') {
      setTourState((s) => ({ ...s, stepIndex: s.stepIndex + 1 }))
    }
  })

  // only show if there are not other modals open
  const { active } = useShowWithPriority(500)

  React.useEffect(() => {
    if (active) {
      makeRequest('/frontend_api/markOnboardingComplete', { onboarding: 'tutorial' }).catch(showAxiosErrorAsToast)
      setState((s) => s && { ...s, user: s.user && { ...s.user, showTutorial: false } })
    }
  }, [active])

  if (!active) return null

  return <Tour state={tourState} setState={setTourState} steps={tourSteps} />
}

const getTourSteps = (
  user: User,
  state: AppState,
  tr: (key: string) => string,
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
): Step[] =>
  user.type === 'teacher'
    ? getTeacherTourSteps(user, state, tr, setComposeState)
    : getStudentGuardianTourSteps(user, state, tr, setComposeState)

const getTeacherTourSteps = (
  user: Teacher,
  state: AppState,
  tr: (key: string) => string,
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
): Step[] => {
  return [
    {
      title: `Welcome to ${state.appName}!`,
      body: `Welcome to ${state.appName}! Where you can connect to the families and students you work with on the platforms they use in the languages they speak🌟`,
    },
    {
      title: 'Sending Messages',
      body: `Let's practice by sending a test message. Click here to start a new message!`,
      target: '#tutorial_compose',
      clickTargetToAdvance: true,
    },
    {
      title: 'Message Types',
      body: `Here you can pick the type of message you want to send. You can send an email, text, call, or multiple at once. If a recipient does not have a particular contact method, it will fallback to a different contact method.`,
      target: '#tutorial_typeSelector',
    },
    {
      title: 'Changing Message Types',
      body: `Click here to change the message type to both email and text.`,
      target: '#tutorial_typeSelectorText',
      clickTargetToAdvance: true,
    },
    {
      title: 'Selecting Students',
      body: `Here you can pick the students this message is regarding. We filled it out for you this time!`,
      target: '#tutorial_recipientSelector',
      onBefore: () => {
        setComposeState(
          (s) =>
            s && {
              ...s,
              selectedRecipients: [user.testStudent!],
              recipientsInfo: {
                ...s.recipientsInfo,
                individuals: [
                  {
                    studentID: user.testStudent!.id,
                  },
                ],
              },
            }
        )
      },
    },
    {
      title: 'Selecting Recipients',
      body: `Here you can pick who you want to send the message to. You can send it to students, guardians, teachers, or multiple at once.`,
      target: '#tutorial_recipientInfoSelector',
    },
    {
      title: 'Writing Messages',
      body: `Here you can write your message.  We filled it out for you this time! You can also attach files and images below.`,
      target: '#tutorial_messageBody',
      onBefore: () => {
        setComposeState(
          (s) =>
            s && {
              ...s,
              subject: `Testing ${state.appName}`,
              bodyHTML: 'Your student is doing great in class!🎉',
            }
        )
      },
    },
    {
      title: 'Previewing Messages',
      body: `Click here to preview your message!`,
      target: '#tutorial_preview',
      clickTargetToAdvance: true,
    },
    {
      title: 'Previewing Messages',
      body: `Here you can see what your message will look like to its recipients. Click outside the preview to close it.`,
      target: '#tutorial_previewBody',
      side: 'left',
      clickTargetToAdvance: true,
    },
    {
      title: 'Sending Messages',
      body: `Click here to send your message!`,
      target: '#tutorial_send',
      clickTargetToAdvance: true,
    },
    {
      title: 'Waiting for Response',
      body: `Your message has been sent! You should receive a response in the next couple seconds.`,
      target: '#tutorial_middle',
      hideNextButton: true,
      side: 'right',
    },
    {
      title: 'Viewing Messages',
      body: `Click the response to see the message thread.`,
      target: '.messagebase',
      clickTargetToAdvance: true,
    },
    {
      title: 'Viewing Threads',
      body: `Here you can see the message thread. You can reply to the message or share it with more people.`,
      target: '#tutorial_conversation',
      side: 'left',
    },
    {
      title: 'Next Steps',
      body: (
        <>
          Thats it! You're ready to start using {state.appName}! Explore other features that save time and enhance
          parent engagement:
          <ul>
            <li>
              🏡
              <a
                className="ml-2"
                href="https://reachmyteach.zendesk.com/hc/en-us/articles/6418269600665"
                target="_blank"
              >
                First Message Home
              </a>
            </li>
            <li>
              📩
              <a
                className="ml-2"
                href="https://reachmyteach.zendesk.com/hc/en-us/articles/17895365900057-Can-I-respond-from-my-email-"
                target="_blank"
              >
                Responding from your email
              </a>
            </li>
            <li>
              👥
              <a
                className="ml-2"
                href="https://reachmyteach.zendesk.com/hc/en-us/articles/17894160001049-Creating-editing-and-sharing-groups-of-students"
                target="_blank"
              >
                Creating Custom Groups
              </a>
            </li>
            <li>
              💌
              <a
                className="ml-2"
                href="https://reachmyteach.zendesk.com/hc/en-us/articles/5655845947417-Personalizing-mesages"
                target="_blank"
              >
                Personalizing Messages
              </a>
            </li>
            <li>
              📢
              <a
                className="ml-2"
                href="https://reachmyteach.zendesk.com/hc/en-us/articles/5185111427609-Sharing-Messages-With-Other-Teachers"
                target="_blank"
              >
                Sharing Messages with other teachers
              </a>
            </li>
            <li>
              🌐
              <a
                className="ml-2"
                href="https://reachmyteach.zendesk.com/hc/en-us/articles/15534126103705-Accessing-On-Demand-Interpreters"
                target="_blank"
              >
                Accessing Interpreters
              </a>
            </li>
            <li>
              📋
              <a
                className="ml-2"
                href="https://reachmyteach.zendesk.com/hc/en-us/categories/4962380065049"
                target="_blank"
              >
                And More
              </a>
            </li>
          </ul>
        </>
      ),
    },
  ]
}

const getStudentGuardianTourSteps = (
  user: Student | Guardian,
  state: AppState,
  tr: (key: string) => string,
  setComposeState: React.Dispatch<React.SetStateAction<ComposeState | undefined>>
): Step[] => {
  return [
    {
      title: state.appName + ' ' + tr(`Walkthrough!`),
      body: tr(`Welcome to ${state.appName}! The new portal for teachers (made by a teacher and his student) to make communicating
        easy between teachers and students/families. Let's take a look around!`),
    },
    {
      title: tr('Navigating and Filtering'),
      body: tr(`It looks just like an email program to make life easy for you. Over here you can create a new message and see
        your inbox/sent mailboxes. You can also filter messages by teacher.`),
      target: '#tutorial_left',
      side: 'right',
    },
    {
      title: tr('Inbox and Sent'),
      body: tr(`In the middle is where you can see your correspondence. You will see what type of correspondence it was
        (email/text/phone), and a brief little bit about the conversation. It will say a teachers's name so you know who the conversation was from.`),
      target: '#tutorial_middle',
      side: 'left',
    },
    {
      title: tr('Viewing Conversations'),
      body: tr(`When you click a message you will see all the messages in that thread and be able to reply!`),
      target: '#tutorial_middle',
      side: 'left',
    },
    {
      title: tr('Filtering Messages'),
      body: tr(`Filter by type of message or date, or you can search to find particular people or content.`),
      target: '#tutorial_filters',
    },
    {
      title: tr('Sending Messages'),
      body: tr(`Let's practice by sending a test message. Click here to start a new message!`),
      target: '#tutorial_compose',
      clickTargetToAdvance: true,
    },
    {
      title: tr('Sending Messages'),
      body: tr(`Here you can send a message to your teacher.`),
      target: '#tutorial_composeModal',
    },
  ]
}
